import type { ReactNode } from 'react';

import { Glossed } from '@/components/glossary';
import { cn } from '@/lib/utils';

export interface WikiTocEntry {
  /** Anchor id (rendered as `#id`) — must match the article's `id` attr. */
  id: string;
  /** Display label in the TOC. Short — single line, no wrap. */
  label: string;
  /** Optional small tag rendered before the label (e.g. "AA01"). */
  tag?: string;
}

/** Two-column wiki layout: sticky TOC on the left, article column on
 *  the right. Used by /rules and /rule-sets to give each page a long-
 *  form scrollable feel with quick in-page navigation.
 *
 *  The TOC collapses below the content on small screens so the wiki is
 *  still readable on tablets. No fancy scroll-spy yet — the anchors are
 *  plain `<a href="#id">` so the browser's native scroll-into-view fires
 *  on click and back/forward works.
 */
export function WikiLayout({
  title,
  description,
  toc,
  children,
}: {
  title: string;
  description?: ReactNode;
  toc: WikiTocEntry[];
  children: ReactNode;
}) {
  return (
    <div className="grid gap-8 lg:grid-cols-[14rem_minmax(0,1fr)]">
      <aside className="hidden lg:block">
        <div className="sticky top-8">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-slate/80">
            On this page
          </p>
          <nav className="grid gap-1">
            {toc.map((entry) => (
              <a
                key={entry.id}
                href={`#${entry.id}`}
                className="group flex items-baseline gap-2 rounded-md px-2 py-1 text-sm text-brand-slate transition-colors hover:bg-secondary hover:text-brand-royal"
              >
                {entry.tag && (
                  <span className="font-mono text-xs text-brand-slate/70 group-hover:text-brand-royal">
                    {entry.tag}
                  </span>
                )}
                <span className="line-clamp-1">{entry.label}</span>
              </a>
            ))}
          </nav>
        </div>
      </aside>
      <div className="min-w-0">
        <header className="mb-8 border-b border-border pb-6">
          <h2 className="text-3xl font-semibold tracking-tight text-brand-navy">{title}</h2>
          {description && (
            <p className="mt-2 max-w-2xl text-sm text-brand-slate">{description}</p>
          )}
        </header>
        <div className="grid gap-12">{children}</div>
      </div>
    </div>
  );
}

/** A wiki section block — title + content. Sub-headings within an
 *  article. Keep these small + consistent so the page reads like prose,
 *  not a settings panel. */
export function WikiSection({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('grid gap-2', className)}>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-brand-slate">
        {title}
      </h4>
      {children}
    </section>
  );
}

/** Render multi-paragraph YAML prose: blank lines split paragraphs,
 *  single newlines within a paragraph collapse to spaces so the
 *  YAML's soft-wraps render as flow. Each paragraph runs through the
 *  glossary so known acronyms get hover-to-define tooltips on first
 *  occurrence. */
export function WikiProse({ text, className }: { text: string; className?: string }) {
  const paragraphs = text
    .trim()
    .split(/\n\s*\n/)
    .map((p) => p.replace(/\s*\n\s*/g, ' ').trim())
    .filter(Boolean);
  return (
    <div className={cn('grid gap-3 text-sm leading-relaxed text-brand-slate', className)}>
      {paragraphs.map((p, i) => (
        <p key={i}>
          <Glossed text={p} />
        </p>
      ))}
    </div>
  );
}
